import React, { useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import {
  Button, Col, Input, Row, Space, Table, Tag, Typography, message,
} from 'antd';
import { ScanOutlined } from '@ant-design/icons';
import { useLazyQuery } from 'hooks/apollo';
import { maskCategoryForEntity } from 'shared/utils/piiScan';
import { regulationForEntityType } from '../FlaggedComments/RedactPiiScanPersonalInfo/entityTypes';
import '../FlaggedComments/RedactPiiScanPersonalInfo/redact-pii-scan-personal-info.less';

const TEST_PII_SCAN = gql`
  query TestPiiScan($text: String!, $settings: JSON) {
    testPiiScan(text: $text, settings: $settings) {
      model
      rawEntityCount
      flaggedEntityCount
      entities
      filteredOut
    }
  }
`;

function isSensitiveRegulation(regulation) {
  return regulation === 'Art. 9(1)';
}

function formatConfidence(confidence) {
  if (confidence == null || Number.isNaN(Number(confidence))) {
    return '—';
  }
  return `${Math.round(Number(confidence) * 100)}%`;
}

function buildTextSegments(text, entities, settings, anonymize = false) {
  if (!text) {
    return [];
  }

  const sorted = [...entities]
    .filter((e) => e.start != null && e.end != null && e.end > e.start)
    .sort((a, b) => a.start - b.start);

  const segments = [];
  let cursor = 0;

  sorted.forEach((entity) => {
    if (entity.start < cursor) {
      return;
    }
    if (entity.start > cursor) {
      segments.push({ kind: 'text', value: text.slice(cursor, entity.start) });
    }
    segments.push({
      kind: 'entity',
      value: anonymize
        ? maskCategoryForEntity(entity, settings)
        : text.slice(entity.start, entity.end),
      entity,
    });
    cursor = entity.end;
  });

  if (cursor < text.length) {
    segments.push({ kind: 'text', value: text.slice(cursor) });
  }

  if (segments.length === 0) {
    return [{ kind: 'text', value: text }];
  }

  return segments;
}

function TextPreview({ text, entities, settings, anonymize = false, className }) {
  const segments = useMemo(
    () => buildTextSegments(text, entities, settings, anonymize),
    [text, entities, settings, anonymize],
  );

  return (
    <div className={className}>
      {segments.map((segment, index) => {
        if (segment.kind === 'text') {
          return <span key={`text-${index}`}>{segment.value}</span>;
        }

        const { entity } = segment;
        const sensitive = isSensitiveRegulation(
          entity.regulation || regulationForEntityType(entity.type),
        );
        return (
          <span
            key={`entity-${entity.id}-${index}`}
            className={`pii-scan-redaction__entity pii-scan-redaction__entity--${sensitive ? 'sensitive' : 'standard'}`}
          >
            {segment.value}
          </span>
        );
      })}
    </div>
  );
}

function mapEntities(entities = []) {
  return entities.map((entity, index) => ({
    id: `${entity.type}-${entity.start ?? 'x'}-${entity.end ?? 'x'}-${index}`,
    text: entity.entity != null ? String(entity.entity) : '',
    type: entity.type || 'PII',
    maskCategory: entity.maskCategory || null,
    regulation: entity.regulation || null,
    confidence: entity.confidence ?? null,
    start: entity.start ?? null,
    end: entity.end ?? null,
  }));
}

const PiiScanTestCard = ({ settings }) => {
  const [text, setText] = useState('');
  const [runTest, { data, loading, error }] = useLazyQuery(TEST_PII_SCAN, {
    fetchPolicy: 'no-cache',
  });

  const result = data?.testPiiScan;
  const flaggedEntities = useMemo(
    () => mapEntities(result?.entities),
    [result?.entities],
  );
  const filteredOutEntities = useMemo(
    () => mapEntities(result?.filteredOut),
    [result?.filteredOut],
  );

  const onScan = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      message.warning('Enter text to scan');
      return;
    }

    try {
      await runTest({
        variables: {
          text: trimmed,
          settings,
        },
      });
    } catch (err) {
      message.error(err.message || 'PII scan failed');
    }
  };

  const entityColumns = [{
    dataIndex: 'text',
    title: 'Text',
    render: (value, record) => {
      const sensitive = isSensitiveRegulation(
        record.regulation || regulationForEntityType(record.type),
      );
      return (
        <Tag
          className="pii-scan-redaction__entity-tag"
          color={sensitive ? 'orange' : 'blue'}
        >
          {value}
        </Tag>
      );
    },
  }, {
    dataIndex: 'type',
    title: 'Type',
    width: 180,
    render: (type) => <Typography.Text code>{type}</Typography.Text>,
  }, {
    dataIndex: 'maskCategory',
    title: 'Mask',
    width: 120,
    render: (_maskCategory, record) => (
      <Typography.Text code>{maskCategoryForEntity(record, settings)}</Typography.Text>
    ),
  }, {
    dataIndex: 'confidence',
    title: 'Confidence',
    align: 'right',
    width: 100,
    render: (confidence) => formatConfidence(confidence),
  }];

  return (
    <div className="pii-scan-redaction">
      <Typography.Paragraph type="secondary">
        Test the scanner against sample text using the settings above
        (including unsaved changes).
      </Typography.Paragraph>
      <Input.TextArea
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste or type text to scan for personal data…"
      />
      <Space style={{ marginTop: 12, marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<ScanOutlined />}
          onClick={onScan}
          loading={loading}
        >
          Scan text
        </Button>
        {result && (
          <Typography.Text type="secondary">
            {result.flaggedEntityCount}
            {' '}
            of
            {' '}
            {result.rawEntityCount}
            {' '}
            detected entities would be flagged
            {' '}
            (model:
            {' '}
            {result.model}
            )
          </Typography.Text>
        )}
      </Space>
      {error && (
        <Typography.Paragraph type="danger" style={{ marginBottom: 16 }}>
          {error.message}
        </Typography.Paragraph>
      )}
      {result && (
        <Row gutter={16} className="pii-scan-redaction__layout">
          <Col span={14} className="pii-scan-redaction__main">
            <div className="pii-scan-redaction__section">
              <div className="pii-scan-redaction__section-header">Input</div>
              <TextPreview
                text={text}
                entities={flaggedEntities}
                settings={settings}
                className="pii-scan-redaction__text-box"
              />
            </div>
            <div className="pii-scan-redaction__section">
              <div className="pii-scan-redaction__section-header">Redacted preview</div>
              <TextPreview
                text={text}
                entities={flaggedEntities}
                settings={settings}
                anonymize
                className="pii-scan-redaction__text-box"
              />
            </div>
          </Col>
          <Col span={10} className="pii-scan-redaction__sidebar">
            <div className="pii-scan-redaction__section">
              <div className="pii-scan-redaction__section-header">Flagged entities</div>
              <Table
                className="break-word pii-scan-redaction__entities-table"
                dataSource={flaggedEntities}
                rowKey="id"
                columns={entityColumns}
                pagination={false}
                size="small"
                locale={{ emptyText: 'No entities flagged with current settings' }}
              />
            </div>
            {filteredOutEntities.length > 0 && (
              <div className="pii-scan-redaction__section">
                <div className="pii-scan-redaction__section-header">Filtered out</div>
                <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                  Detected by the model but excluded by detection toggles, combination rules,
                  or confidence thresholds.
                </Typography.Paragraph>
                <Table
                  className="break-word pii-scan-redaction__entities-table"
                  dataSource={filteredOutEntities}
                  rowKey="id"
                  columns={entityColumns}
                  pagination={false}
                  size="small"
                />
              </div>
            )}
          </Col>
        </Row>
      )}
    </div>
  );
};

export default PiiScanTestCard;
